/**
 * Life Journeys — guided experiences framed around consumer LIFE events,
 * not products. Each journey composes the right learning + institutions +
 * questions for that moment.
 */
import { Link, useParams } from "react-router-dom";
import { WalletShell, NAVY, NAVY_DARK, GOLD } from "@/components/wallet/WalletShell";
import { Home as HomeIcon, Baby, Sun, Heart, Briefcase, GraduationCap, ArrowRight, ArrowLeft, Sparkles, Compass, CheckCircle2, HelpCircle } from "lucide-react";
import { JOURNEY_CONTENT } from "@/lib/life-journey-content";

const JOURNEYS = [
  { id: "first_home",   title: "Buying your first home",  icon: HomeIcon,        blurb: "From affordability to bond registration — the whole journey, in plain language.", tone: "#2563EB", steps: 6 },
  { id: "family",       title: "Starting a family",       icon: Baby,            blurb: "Medical aid, life cover, education savings — the protection you actually need, without the upsell.", tone: "#EC4899", steps: 5 },
  { id: "retirement",   title: "Planning for retirement", icon: Sun,             blurb: "How much is enough, what a retirement annuity really costs, and how to compare providers.", tone: "#F59E0B", steps: 7 },
  { id: "bereavement",  title: "After a bereavement",     icon: Heart,           blurb: "The forms to fill, the accounts to close, the claims to submit — one calm checklist.", tone: "#94A3B8", steps: 5 },
  { id: "business",     title: "Starting a business",     icon: Briefcase,       blurb: "Registering the entity, opening the bank account, and the insurance you must not skip.", tone: "#059669", steps: 6 },
  { id: "education",    title: "Planning for education",  icon: GraduationCap,   blurb: "TFSA, endowment, RA — which savings vehicle actually beats inflation over 18 years?", tone: "#7C3AED", steps: 5 },
];

export default function WalletLifeJourneys() {
  const { id } = useParams();
  const selected = id ? JOURNEYS.find(j => j.id === id) : null;
  if (selected) return <JourneyDetail journey={selected} />;
  return (
    <WalletShell activeTab="journeys">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-10" data-testid="wallet-life-journeys">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Section</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>Life Journeys</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            The financial parts of the life moments that actually matter. Guided experiences — not articles.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="life-journey-list">
          {JOURNEYS.map(j => <JourneyCard key={j.id} j={j} />)}
        </div>

        <ComingSoonBanner />
      </div>
    </WalletShell>
  );
}

function JourneyCard({ j }) {
  const Icon = j.icon;
  return (
    <Link to={`/wallet/life-journeys/${j.id}`} data-testid={`life-journey-${j.id}`} className="group rounded-3xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition relative" style={{ background: `linear-gradient(160deg, ${NAVY} 0%, ${NAVY_DARK} 90%)` }}>
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20 group-hover:opacity-30 transition" style={{ background: j.tone }} />
      <div className="relative p-6 text-white h-full flex flex-col">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${j.tone}30` }}>
          <Icon className="w-5 h-5" style={{ color: j.tone }} />
        </div>
        <div className="mt-6 font-display text-xl font-semibold">{j.title}</div>
        <div className="mt-2 text-[13px] text-white/70 leading-relaxed flex-1">{j.blurb}</div>
        <div className="mt-5 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-white/50">{j.steps} step guided journey</span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: GOLD }}>Begin <ArrowRight className="w-3 h-3" /></span>
        </div>
      </div>
    </Link>
  );
}

function ComingSoonBanner() {
  return (
    <div className="rounded-2xl p-5" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}55` }} data-testid="life-journey-coming-soon">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
        <div>
          <div className="text-sm font-semibold" style={{ color: NAVY }}>More journeys coming</div>
          <div className="text-xs text-slate-700 mt-1 leading-relaxed">
            Divorce & separation · Moving countries · Losing a job · Inheriting assets · Caring for an ageing parent. Suggest what should come next via <em>Ask NAVI</em>.
          </div>
        </div>
      </div>
    </div>
  );
}

function JourneyDetail({ journey }) {
  const Icon = journey.icon;
  const content = JOURNEY_CONTENT[journey.id];
  const steps = content?.steps || [];
  return (
    <WalletShell activeTab="journeys">
      <div className="max-w-4xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-8" data-testid={`life-journey-detail-${journey.id}`}>
        <Link to="/wallet/life-journeys" className="inline-flex items-center gap-1 text-xs" style={{ color: NAVY }}>
          <ArrowLeft className="w-3.5 h-3.5" /> All life journeys
        </Link>
        <section className="rounded-3xl overflow-hidden relative" style={{ background: `linear-gradient(160deg, ${NAVY} 0%, ${NAVY_DARK} 90%)` }}>
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25" style={{ background: journey.tone }} />
          <div className="relative p-8 lg:p-12 text-white">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${journey.tone}30` }}>
              <Icon className="w-6 h-6" style={{ color: journey.tone }} />
            </div>
            <h1 className="mt-6 font-display text-3xl lg:text-4xl font-semibold">{journey.title}</h1>
            <p className="mt-3 text-white/70 max-w-xl leading-relaxed">{content?.intro || journey.blurb}</p>
            <div className="mt-4 text-[10px] uppercase tracking-widest text-white/50">{steps.length} steps · plain language · South African rules</div>
          </div>
        </section>

        {steps.length === 0 && (
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-8 text-center text-sm text-slate-500">
            Content for this journey is being curated. Ask NAVI in the meantime.
          </div>
        )}

        <ol className="space-y-5">
          {steps.map((s, i) => (
            <li key={i} data-testid={`journey-step-${i + 1}`} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 lg:p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-display font-semibold text-white text-lg" style={{ background: journey.tone }}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-lg lg:text-xl font-semibold" style={{ color: NAVY }}>{s.title}</h3>
                  <p className="text-[14px] text-slate-700 leading-relaxed mt-2">{s.body}</p>

                  {s.checklist && s.checklist.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">What to do</div>
                      <ul className="space-y-1.5">
                        {s.checklist.map((c, j) => (
                          <li key={j} className="flex items-start gap-2 text-[13px] text-slate-700">
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: journey.tone }} />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.questions && s.questions.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Ask NAVI</div>
                      <div className="flex flex-wrap gap-2">
                        {s.questions.map((q, j) => (
                          <button
                            key={j}
                            data-testid={`ask-navi-${i + 1}-${j}`}
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent("navi.open", { detail: { question: q } }));
                            }}
                            className="inline-flex items-center gap-1 rounded-full text-[11px] px-3 py-1.5 border transition hover:shadow-sm"
                            style={{ background: `${GOLD}15`, borderColor: `${GOLD}55`, color: NAVY }}
                          >
                            <HelpCircle className="w-3 h-3" /> {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="rounded-2xl p-5" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}55` }} data-testid="journey-detail-note">
          <div className="flex items-start gap-3">
            <Compass className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
            <div className="text-xs text-slate-700 leading-relaxed">
              This journey explains only — it doesn&apos;t recommend or advise. Every rand figure and every SA rule is grounded in publicly-available law and current market practice. When you have a specific quote in front of you, run it through NAVI on the review page for a document-grounded read.
            </div>
          </div>
        </div>
      </div>
    </WalletShell>
  );
}
